import { useEffect, useMemo } from "react";
import { Division, Flex, Heading, Skeleton, useToast } from "@ir/ui";
import { Outlet, useNavigate } from "react-router";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  PageContent,
  PageHeader,
} from "@src/presentation/components/Layout/common";
import { type BreadcrumbItem } from "@src/presentation/components/common";
import {
  MasterDataBrandProvider,
  useMasterDataBrand,
} from "./MasterDataBrandContext";
import { MasterDataOrgBrandSelector } from "./utils/MasterDataOrgBrandSelector";
import { MasterDataSubNav } from "./utils/MasterDataSubNav";
import {
  getMasterDataSectionLabel,
  isMasterDataSection,
} from "./masterDataNav.config";
import { isProductCatalogSection } from "./sections/productCatalogSections";
import { isRetailersSection } from "./sections/retailersSections";
import { isTradingPartnersSection } from "./sections/tradingPartnersSections";
import { isImplementedDeductionSetupSection } from "./sections/deductionSetupSections";
import "./MasterData.scss";

const MasterDataLayoutContent = () => {
  const navigate = useNavigate();
  const { error: errorToast } = useToast();
  const {
    organizationKey,
    brandKey,
    organization,
    brand,
    section,
    isInvalidBrandSelection,
    isLookupReady,
    isLookupLoading,
  } = useMasterDataBrand();

  useEffect(() => {
    if (!isLookupReady || !isInvalidBrandSelection) {
      return;
    }
    errorToast({
      content: "Selected organization or brand was not found.",
    });
    navigate("/master-data", { replace: true });
  }, [errorToast, isInvalidBrandSelection, isLookupReady, navigate]);

  const showSubNav =
    Boolean(organizationKey && brandKey) && !isInvalidBrandSelection;

  const isListSection =
    Boolean(section) &&
    isMasterDataSection(section ?? undefined) &&
    (isTradingPartnersSection(section ?? undefined) ||
      isProductCatalogSection(section ?? undefined) ||
      isRetailersSection(section ?? undefined) ||
      isImplementedDeductionSetupSection(section ?? undefined));

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const root: BreadcrumbItem = {
      key: "master-data-root",
      title: "Master Data",
      path: organizationKey && brandKey ? "/master-data" : undefined,
    };

    if (!organizationKey || !brandKey) {
      return [root];
    }

    const brandLabel = brand?.brandDisplayName ?? brand?.brandName ?? brandKey;

    const brandTitle =
      isLookupLoading && !brand ? (
        <Skeleton height={16} width={160} animation="wave" variant="rounded" />
      ) : (
        `${brandLabel}`
      );

    const brandCrumb: BreadcrumbItem = {
      key: `master-data-brand-${organizationKey}-${brandKey}`,
      title: brandTitle,
      path: section ? `/master-data/${organizationKey}/${brandKey}` : undefined,
    };

    if (!section) {
      return [root, brandCrumb];
    }

    return [
      root,
      brandCrumb,
      {
        key: `master-data-section-${section}`,
        title: getMasterDataSectionLabel(section) ?? section,
      },
    ];
  }, [
    organizationKey,
    brandKey,
    brand,
    organization,
    section,
    isLookupLoading,
  ]);

  return (
    <>
      <PageHeader breadcrumb={{ items: breadcrumbItems }}>
        <Flex align="center" gap={1} justifyContent="space-between">
          <Heading level={5} className="text-grey-700 font-bold m-0">
            Master Data
          </Heading>
          <MasterDataOrgBrandSelector />
        </Flex>
      </PageHeader>
      <PageContent
        className={`p-4 master-data-page-content${showSubNav && isListSection ? " master-data-page-content--list" : ""}`}
      >
        {showSubNav ? (
          <Division className="master-data-layout">
            <Division className="master-data-layout__sidebar">
              <MasterDataSubNav />
            </Division>
            <Division className="master-data-layout__content">
              <Outlet />
            </Division>
          </Division>
        ) : (
          <Outlet />
        )}
      </PageContent>
    </>
  );
};

export const MasterDataLayout = () => (
  <ErrorBoundary>
    <MasterDataBrandProvider>
      <MasterDataLayoutContent />
    </MasterDataBrandProvider>
  </ErrorBoundary>
);
